export const normalize = value => String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
const tokensOf = value => String(value).normalize('NFKD').toLowerCase().match(/[a-z]+|\d+/g) || [];

export function parseQuery(value) {
  const tokens = tokensOf(value);
  const years = tokens.filter(token => /^(19|20)\d{2}$/.test(token));
  return { year: years.length === 1 ? Number(years[0]) : null, invalidYears: years.length > 1,
    tokens: tokens.filter(token => !/^(19|20)\d{2}$/.test(token)) };
}

export function buildIndex(data) {
  if (data.version !== 1 || !Array.isArray(data.models) || !Array.isArray(data.paths)) throw new Error('Invalid vehicle index');
  return data.models.map(([make, model, rows]) => ({ make, model, rows, name: `${make} ${model}`,
    compact: normalize(`${make} ${model}`), tokens: [...new Set([...tokensOf(`${make} ${model}`), ...tokensOf(normalize(make)), ...tokensOf(normalize(model))])] }));
}

export function yearsForModel(model) {
  const years = new Set();
  for (const [start, end] of model.rows) for (let year = start; year <= end; year++) years.add(year);
  return [...years].sort((a, b) => b - a);
}

export function routeForYear(model, year, paths) {
  const matches = model.rows.filter(([start, end]) => year >= start && year <= end)
    .sort((a, b) => (a[1] - a[0]) - (b[1] - b[0]));
  if (!matches.length) return null;
  const path = paths[matches[0][2]];
  return /^\/(?:collections\/[a-z0-9-]+(?:\/[a-z0-9-]+)?|products\/[a-z0-9-]+)$/.test(path) ? path : null;
}

export function searchModels(models, value) {
  const query = parseQuery(value);
  if (!query.tokens.length || query.invalidYears) return { query, matches: [], yearUnavailable: false };
  const compact = query.tokens.join('');
  const candidates = models.filter(model => query.tokens.every(token =>
    model.tokens.some(word => /^\d+$/.test(token) ? word === token : word.startsWith(token)))
    || model.compact === compact);
  const dated = query.year ? candidates.filter(model => model.rows.some(([start, end]) => query.year >= start && query.year <= end)) : candidates;
  const yearUnavailable = Boolean(query.year && candidates.length && !dated.length);
  const matches = (yearUnavailable ? candidates : dated).sort((a, b) => {
    const score = model => (model.compact === compact ? 1000 : 0)
      + query.tokens.filter(token => model.tokens.includes(token)).length * 20 - model.tokens.length;
    return score(b) - score(a) || a.name.localeCompare(b.name, 'nl', { numeric: true });
  });
  return { query, matches, yearUnavailable };
}

if (typeof document !== 'undefined') {
  const settings = document.getElementById('mxpro-motor-search-settings');
  if (settings) start(JSON.parse(settings.textContent));
}

function start(settings) {
  let dataPromise;
  let serial = 0;
  const attached = new WeakSet();
  const text = (key, vars = {}) => Object.entries(vars).reduce((value, [name, replacement]) => value.replaceAll(`{${name}}`, replacement), settings[key] || '');
  const node = (tag, className, content) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (content) el.textContent = content;
    return el;
  };
  const load = () => {
    if (!dataPromise) dataPromise = fetch(settings.indexUrl).then(response => {
      if (!response.ok) throw new Error('Vehicle index unavailable');
      return response.json();
    }).then(data => ({ data, models: buildIndex(data) })).catch(error => { dataPromise = null; throw error; });
    return dataPromise;
  };

  function attach(container) {
    if (attached.has(container) || container.closest('.easysearch-garage-holder')) return;
    if (!container.querySelector('[id="easysearch_field_28411"]')) return;
    attached.add(container);
    const id = `mxpro-motor-search-${++serial}`;
    const section = node('section', 'mxpro-motor-search');
    section.setAttribute('aria-label', text('title'));
    const label = node('label', 'mxpro-motor-search__label', text('title'));
    label.htmlFor = id;
    const intro = node('p', 'mxpro-motor-search__intro', text('intro'));
    const form = node('form', 'mxpro-motor-search__form');
    form.setAttribute('role', 'search');
    form.setAttribute('aria-label', text('title'));
    const input = node('input', 'mxpro-motor-search__input');
    input.type = 'search';
    input.id = id;
    input.placeholder = text('placeholder');
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('aria-describedby', `${id}-help ${id}-status`);
    const submit = node('button', 'mxpro-motor-search__submit', text('search'));
    submit.type = 'submit';
    form.append(input, submit);
    const help = node('p', 'mxpro-motor-search__help', text('hint'));
    help.id = `${id}-help`;
    const status = node('p', 'mxpro-motor-search__status', text('loading'));
    status.id = `${id}-status`;
    status.setAttribute('role', 'status');
    const results = node('ul', 'mxpro-motor-search__results');
    results.setAttribute('aria-label', text('matches'));
    const selection = node('div', 'mxpro-motor-search__selection');
    selection.hidden = true;
    const chosen = node('strong', 'mxpro-motor-search__chosen');
    const yearLabel = node('label', 'mxpro-motor-search__year-label', text('chooseYear'));
    yearLabel.htmlFor = `${id}-year`;
    const yearSelect = node('select', 'mxpro-motor-search__year');
    yearSelect.id = `${id}-year`;
    const yearStatus = node('p', 'mxpro-motor-search__year-status');
    yearStatus.setAttribute('role', 'status');
    const go = node('a', 'mxpro-motor-search__go', text('viewParts'));
    go.hidden = true;
    const change = node('button', 'mxpro-motor-search__change', text('change'));
    change.type = 'button';
    selection.append(chosen, yearLabel, yearSelect, yearStatus, go, change);
    const manual = node('button', 'mxpro-motor-search__manual', text('manual'));
    manual.type = 'button';
    manual.setAttribute('aria-expanded', 'false');
    section.append(label, intro, form, help, status, results, selection, manual);
    container.prepend(section);
    let models = [];
    let paths = [];
    let selectedModel = null;
    let currentMatches = [];
    let lastQuery = null;
    let limit = 8;

    function updateYear() {
      const year = Number(yearSelect.value);
      const path = selectedModel && yearSelect.value && routeForYear(selectedModel, year, paths);
      go.hidden = !path;
      if (path) {
        go.href = path;
        go.textContent = text('viewPartsFor', { motor: selectedModel.name, year });
        yearStatus.textContent = text('ready');
      } else {
        go.removeAttribute('href');
        yearStatus.textContent = text('yearRequired');
      }
    }

    function choose(model) {
      selectedModel = model;
      chosen.textContent = model.name;
      results.replaceChildren();
      status.textContent = text('motorChosen');
      yearSelect.replaceChildren(new Option(text('chooseYear'), ''));
      for (const year of yearsForModel(model)) yearSelect.add(new Option(String(year), String(year)));
      const requestedYear = lastQuery?.year;
      yearSelect.value = requestedYear && yearsForModel(model).includes(requestedYear) ? String(requestedYear) : '';
      selection.hidden = false;
      updateYear();
      if (yearSelect.value) go.focus(); else yearSelect.focus();
    }

    function render() {
      selection.hidden = true;
      selectedModel = null;
      go.hidden = true;
      go.removeAttribute('href');
      const result = searchModels(models, input.value);
      lastQuery = result.query;
      currentMatches = result.matches;
      results.replaceChildren();
      if (!input.value.trim()) { status.textContent = text('start'); return; }
      if (result.query.invalidYears) { status.textContent = text('oneYear'); return; }
      if (!result.query.tokens.length) { status.textContent = text('needModel'); return; }
      if (!currentMatches.length) { status.textContent = text('noMatch'); return; }
      const prefix = result.yearUnavailable ? text('yearUnavailable', { year: result.query.year }) + ' ' : '';
      status.textContent = prefix + text('found', { count: currentMatches.length });
      currentMatches.slice(0, limit).forEach(model => {
        const li = node('li');
        const button = node('button', 'mxpro-motor-search__option');
        button.type = 'button';
        button.append(node('strong', '', model.name));
        const years = yearsForModel(model);
        button.append(node('span', '', result.query.year && !result.yearUnavailable ? String(result.query.year)
          : text('years', { first: years.at(-1), last: years[0] })));
        button.addEventListener('click', () => choose(model));
        li.append(button);
        results.append(li);
      });
      if (currentMatches.length > limit) {
        const li = node('li');
        const more = node('button', 'mxpro-motor-search__more', text('more'));
        more.type = 'button';
        more.addEventListener('click', () => { limit += 8; render(); results.querySelectorAll('.mxpro-motor-search__option')[limit - 16]?.focus(); });
        li.append(more);
        results.append(li);
      }
    }

    input.addEventListener('input', () => { limit = 8; render(); });
    input.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown') { event.preventDefault(); results.querySelector('button')?.focus(); }
    });
    results.addEventListener('keydown', event => {
      const buttons = [...results.querySelectorAll('button')];
      const index = buttons.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') { event.preventDefault(); buttons[Math.min(index + 1, buttons.length - 1)]?.focus(); }
      if (event.key === 'ArrowUp') { event.preventDefault(); if (index <= 0) input.focus(); else buttons[index - 1]?.focus(); }
      if (event.key === 'Escape') input.focus();
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (currentMatches.length === 1) choose(currentMatches[0]);
      else { render(); results.querySelector('button')?.focus(); }
    });
    yearSelect.addEventListener('change', updateYear);
    change.addEventListener('click', () => { render(); input.focus(); });
    go.addEventListener('click', event => {
      const year = Number(yearSelect.value);
      const path = selectedModel && routeForYear(selectedModel, year, paths);
      if (!path || !yearSelect.value) { event.preventDefault(); updateYear(); return; }
      try { sessionStorage.setItem('mxpro-ymm-selection-v1', JSON.stringify({make: selectedModel.make, model: selectedModel.model, year, path})); } catch (_) {}
    });
    manual.addEventListener('click', () => {
      const open = container.classList.toggle('mxpro-motor-search--manual');
      manual.setAttribute('aria-expanded', String(open));
      manual.textContent = text(open ? 'closeManual' : 'manual');
    });
    load().then(result => {
      models = result.models;
      paths = result.data.paths;
      container.classList.add('mxpro-motor-search--ready');
      render();
    }).catch(() => {
      status.textContent = text('loadError');
      form.hidden = true;
      manual.hidden = true;
    });
  }

  function discover(root) {
    if (root.nodeType !== 1) return;
    if (root.matches('.easysearch-container')) attach(root);
    root.querySelectorAll('.easysearch-container').forEach(attach);
    const parent = root.closest('.easysearch-container');
    if (parent) attach(parent);
  }
  discover(document.documentElement);
  new MutationObserver(records => {
    for (const record of records) for (const child of record.addedNodes) discover(child);
  }).observe(document.body, { childList: true, subtree: true });
}

import('https://feedservermxprostoreparts.com/finder/v1/widget.js?v=c5233ebe28cb').catch(()=>{});
